import React, { useEffect, useRef, useState } from 'react';
import { useMessageStore } from '../../store/messageStore';
import { useServerStore } from '../../store/serverStore';
import { useAuthStore } from '../../store/authStore';
import { format, isToday, isYesterday, differenceInCalendarDays, startOfDay, isValid } from 'date-fns';
import { Edit, Trash2, Reply, Smile, X, Flag, Plus, Copy } from 'lucide-react';
import ReportModal from '../moderation/ReportModal';
import EmojiPicker from 'emoji-picker-react';
import ContextMenu, { ContextMenuItem } from '../ui/ContextMenu';
import TypingIndicator from './TypingIndicator';
import { getChannel, joinChatChannel } from '../../lib/realtime';
import { hydrateMessageAuthor } from '../../lib/messageAuthors';
import { applyMessageUpdate, applyReactionUpdate, normalizeMessage } from '../../lib/messageEvents';
import { useDeveloperMode } from '../../lib/developerMode';
import { Permissions, hasPermission } from '../../lib/permissions';

function parseMessageDate(dateStr?: string | null): Date {
  const date = new Date(dateStr || '');
  return isValid(date) ? date : new Date();
}

function snowflakeTimestamp(id?: string | null): number | null {
  if (!id || !/^\d+$/.test(id)) return null;
  try {
    const discordEpoch = 1420070400000n;
    return Number((BigInt(id) >> 22n) + discordEpoch);
  } catch {
    return null;
  }
}

function getMessageSortTime(message: any): number {
  const explicitDate = new Date(message.__sort_at || message.created_at || message.timestamp || message.edited_timestamp || '');
  if (isValid(explicitDate)) return explicitDate.getTime();
  return snowflakeTimestamp(message.id) ?? Date.now();
}

function compareTimelineItems(a: any, b: any): number {
  const timeDiff = getMessageSortTime(a) - getMessageSortTime(b);
  if (timeDiff !== 0) return timeDiff;
  return String(a.id || '').localeCompare(String(b.id || ''));
}

function formatRelativeDate(dateStr?: string | null): { label: string; tooltip: string } {
  const date = parseMessageDate(dateStr);
  const now = new Date();
  const tooltip = format(date, 'dd/MM/yyyy HH:mm:ss');

  if (isToday(date)) {
    return { label: format(date, 'h:mm a'), tooltip };
  }
  if (isYesterday(date)) {
    return { label: `Yesterday at ${format(date, 'h:mm a')}`, tooltip };
  }
  const daysDiff = differenceInCalendarDays(startOfDay(now), startOfDay(date));
  if (daysDiff < 7) {
    return { label: `${format(date, 'EEEE')} at ${format(date, 'h:mm a')}`, tooltip };
  }
  return { label: format(date, 'MMM d, yyyy h:mm a'), tooltip };
}
import MessageInput from './MessageInput';
import UserProfileLink from '../profile/UserProfileLink';
import MessageProcessor from './MessageProcessor';
import DiscordEmbeds from './DiscordEmbeds';
import { ChannelMessageSkeleton } from '../ui/MessageSkeleton';
import AppBadge from '../ui/AppBadge';

const PAGE_SIZE = 40;
const TOP_LOAD_THRESHOLD = 80;
const BOTTOM_STICK_THRESHOLD = 120;

const MessageList: React.FC = () => {
  const { currentServer, currentChannel, members, channels, roles } = useServerStore();
  const { user } = useAuthStore();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [localMessages, setLocalMessages] = useState<any[]>([]);
  const [ephemeralMessages, setEphemeralMessages] = useState<any[]>([]);
  const [pendingInteractions, setPendingInteractions] = useState<any[]>([]);
  const [applicationCommands, setApplicationCommands] = useState<any[]>([]);
  const [pendingComponentId, setPendingComponentId] = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<any | null>(null);
  const [modalValues, setModalValues] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const channelRef = useRef<string | null>(null);
  const [reportingMessage, setReportingMessage] = useState<any | null>(null);
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState<string | null>(null);
  const reactionPickerRef = useRef<HTMLDivElement>(null);
  const initialScrollDoneRef = useRef(false);
  const shouldStickToBottomRef = useRef(true);
  const interactionSortTimesRef = useRef<Record<string, string>>({});
  const pendingComponentInteractionRef = useRef<string | null>(null);
  const pendingComponentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [contextMenu, setContextMenu] = useState<{ position: { x: number; y: number }; messageId: string } | null>(null);
  const developerMode = useDeveloperMode();
  const channelPermissions = typeof currentChannel?.permissions === 'number' ? currentChannel.permissions : 0;
  const canSendMessages = currentChannel ? hasPermission(channelPermissions, Permissions.SEND_MESSAGES) : false;
  const canAttachFiles = currentChannel ? hasPermission(channelPermissions, Permissions.ATTACH_FILES) : false;
  const canAddReactions = currentChannel ? hasPermission(channelPermissions, Permissions.ADD_REACTIONS) : false;
  const canManageMessages = currentChannel ? hasPermission(channelPermissions, Permissions.MANAGE_MESSAGES) : false;
  const getAuthorRoleColor = (userId: string) => {
    const member = (members || []).find((item: any) => item.user_id === userId);
    return [...((member as any)?.roles || [])]
      .filter((role: any) => !role.is_everyone && role.color)
      .sort((a: any, b: any) => (b.position || 0) - (a.position || 0))[0]?.color;
  };

  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
    shouldStickToBottomRef.current = true;
  };

  const loadOlderMessages = async () => {
    if (!currentChannel?.id || isLoading || isLoadingMore || !hasMore || localMessages.length === 0) {
      return;
    }

    const oldestMessage = localMessages[0];
    if (!oldestMessage?.created_at) {
      setHasMore(false);
      return;
    }

    const container = scrollContainerRef.current;
    const previousHeight = container?.scrollHeight ?? 0;
    setIsLoadingMore(true);

    try {
      const olderMessages = await useMessageStore
        .getState()
        .messagesAPI.getChannelMessages(currentChannel.id, {
          limit: PAGE_SIZE,
          before: oldestMessage.id,
        });

      const batch = (olderMessages || []).map(normalizeMessage);
      setHasMore(batch.length === PAGE_SIZE);

      setLocalMessages((prev) => {
        const existingIds = new Set(prev.map((message) => message.id));
        const dedupedBatch = batch.filter((message: any) => !existingIds.has(message.id));
        return dedupedBatch.length > 0 ? [...dedupedBatch, ...prev] : prev;
      });

      requestAnimationFrame(() => {
        const currentContainer = scrollContainerRef.current;
        if (!currentContainer) return;
        const newHeight = currentContainer.scrollHeight;
        currentContainer.scrollTop += newHeight - previousHeight;
      });
    } catch {
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const distanceFromBottom =
      container.scrollHeight - (container.scrollTop + container.clientHeight);
    shouldStickToBottomRef.current = distanceFromBottom <= BOTTOM_STICK_THRESHOLD;

    if (container.scrollTop <= TOP_LOAD_THRESHOLD) {
      void loadOlderMessages();
    }
  };

  // Effect 1: fetch messages when channel changes
  useEffect(() => {
    if (!currentChannel?.id) return;
    if (channelRef.current === currentChannel.id) return;

    channelRef.current = currentChannel.id;
    initialScrollDoneRef.current = false;
    shouldStickToBottomRef.current = true;
    setLocalMessages([]);
    setEphemeralMessages([]);
    setPendingInteractions([]);
    setPendingComponentId(null);
    pendingComponentInteractionRef.current = null;
    if (pendingComponentTimeoutRef.current) clearTimeout(pendingComponentTimeoutRef.current);
    pendingComponentTimeoutRef.current = null;
    interactionSortTimesRef.current = {};
    setHasMore(true);
    setIsLoadingMore(false);
    setIsLoading(true);

    const messageStore = useMessageStore.getState();
    messageStore.joinChannel(currentChannel.id);

    messageStore.messagesAPI.getChannelMessages(currentChannel.id, { limit: PAGE_SIZE })
      .then(messages => {
        const batch = (messages || []).map(normalizeMessage);
        setLocalMessages(batch);
        setHasMore(batch.length === PAGE_SIZE);
      })
      .catch(() => { setLocalMessages([]); })
      .finally(() => { setIsLoading(false); });

    messageStore.messagesAPI.getChannelApplicationCommands(currentChannel.id)
      .then((commands: any[]) => setApplicationCommands(Array.isArray(commands) ? commands : []))
      .catch(() => setApplicationCommands([]));
  }, [currentChannel?.id]);

  useEffect(() => {
    return () => {
      if (pendingComponentTimeoutRef.current) clearTimeout(pendingComponentTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!currentChannel?.id) return;

    const handleInteractionResponse = (event: Event) => {
      const payload = (event as CustomEvent).detail;
      if (!payload || payload.type === 'autocomplete') return;
      if (payload.channel_id && payload.channel_id !== currentChannel.id) return;
      if (payload.type === 'modal' || payload.action === 'modal') {
        if (payload.interaction_id) {
          if (payload.interaction_id === pendingComponentInteractionRef.current) {
            setPendingComponentId(null);
            pendingComponentInteractionRef.current = null;
            if (pendingComponentTimeoutRef.current) clearTimeout(pendingComponentTimeoutRef.current);
            pendingComponentTimeoutRef.current = null;
          }
          setPendingInteractions((prev) => prev.filter((item) => item.interaction_id !== payload.interaction_id));
        }
        setActiveModal(payload);
        setModalValues({});
        return;
      }
      if (payload.interaction_id) {
        if (payload.interaction_id === pendingComponentInteractionRef.current) {
          setPendingComponentId(null);
          pendingComponentInteractionRef.current = null;
          if (pendingComponentTimeoutRef.current) clearTimeout(pendingComponentTimeoutRef.current);
          pendingComponentTimeoutRef.current = null;
        }
        const sortAt = interactionSortTimesRef.current[payload.interaction_id];
        if (sortAt) payload.__sort_at = sortAt;
        setPendingInteractions((prev) => prev.filter((item) => item.interaction_id !== payload.interaction_id));
      }
      if (payload.type === 'ack' || payload.action === 'ack') return;
      setEphemeralMessages((prev) => {
        if (payload.action === 'delete') {
          return prev.filter((message) => message.id !== payload.id);
        }
        if (payload.action === 'update') {
          return prev.map((message) => message.id === payload.id ? { ...message, ...payload } : message);
        }
        if (prev.some((message) => message.id === payload.id)) {
          return prev.map((message) => message.id === payload.id ? { ...message, ...payload } : message);
        }
        return [...prev, payload];
      });
      if (shouldStickToBottomRef.current) {
        requestAnimationFrame(() => scrollToBottom('auto'));
      }
    };

    window.addEventListener('erozion-interaction-response', handleInteractionResponse);
    return () => window.removeEventListener('erozion-interaction-response', handleInteractionResponse);
  }, [currentChannel?.id]);

  useEffect(() => {
    if (!currentChannel?.id) return;

    const handlePendingInteraction = (event: Event) => {
      const payload = (event as CustomEvent).detail;
      if (!payload?.interaction_id || payload.channel_id !== currentChannel.id) return;
      interactionSortTimesRef.current[payload.interaction_id] = payload.created_at || new Date().toISOString();
      setPendingInteractions((prev) => [
        ...prev.filter((item) => item.interaction_id !== payload.interaction_id),
        { ...payload, __pending: true },
      ]);
      window.setTimeout(() => {
        setPendingInteractions((prev) => prev.map((item) => (
          item.interaction_id === payload.interaction_id ? { ...item, failed: true } : item
        )));
      }, 15_000);
      if (shouldStickToBottomRef.current) {
        requestAnimationFrame(() => scrollToBottom('auto'));
      }
    };

    window.addEventListener('erozion-interaction-pending', handlePendingInteraction);
    return () => window.removeEventListener('erozion-interaction-pending', handlePendingInteraction);
  }, [currentChannel?.id]);

  // Effect 2: Phoenix channel listeners — re-bind when channel changes
  useEffect(() => {
    if (!currentChannel?.id) return;

    const channelId = currentChannel.id;
    // Ensure the channel is joined
    joinChatChannel(channelId);
    const channel = getChannel(`channel:${channelId}`);
    if (!channel) return;

    const handleNewMessage = async (incoming: any) => {
      const message = normalizeMessage(await hydrateMessageAuthor(incoming));
      if (message.channel_id !== channelId) return;
      const interactionId = message.interaction_metadata?.id;
      if (interactionId && interactionSortTimesRef.current[interactionId]) {
        message.__sort_at = interactionSortTimesRef.current[interactionId];
        setPendingInteractions((prev) => prev.filter((item) => item.interaction_id !== interactionId));
      }

      if (message.deleted_at) return;

      setLocalMessages(prev => {
        if (prev.some(msg => msg.id === message.id)) {
          return prev;
        }
        return [...prev, message];
      });

      if (shouldStickToBottomRef.current) {
        requestAnimationFrame(() => {
          scrollToBottom('auto');
        });
      }
    };
    const handleMessageUpdate = (message: any) => {
      setLocalMessages(prev => prev.map(msg => msg.id === message.id ? applyMessageUpdate(msg, message) : msg));
    };
    const handleMessageDelete = (data: any) => {
      if (!data.isDm) {
        setLocalMessages(prev => prev.filter(msg => msg.id !== data.id));
      }
    };
    const handleReactionUpdate = (data: any) => {
      if (!data.isDm) {
        const container = scrollContainerRef.current;
        const distFromBottom = container
          ? container.scrollHeight - (container.scrollTop + container.clientHeight)
          : 0;

        setLocalMessages(prev => prev.map(msg =>
          msg.id === data.messageId ? applyReactionUpdate(msg, data) : msg
        ));

        if (container) {
          requestAnimationFrame(() => {
            container.scrollTop = container.scrollHeight - container.clientHeight - distFromBottom;
          });
        }
      }
    };

    const handleTypingStart = (data: any) => {
      if (!data.roomKey) return;
      useMessageStore.setState(state => {
        const current = state.typingUsers[data.roomKey] || [];
        if (current.some(t => t.userId === data.userId)) return state;
        return { typingUsers: { ...state.typingUsers, [data.roomKey]: [...current, { userId: data.userId, username: data.username }] } };
      });
    };
    const handleTypingStop = (data: any) => {
      if (!data.roomKey) return;
      useMessageStore.setState(state => {
        const current = state.typingUsers[data.roomKey] || [];
        return { typingUsers: { ...state.typingUsers, [data.roomKey]: current.filter(t => t.userId !== data.userId) } };
      });
    };

    const newMsgRef = channel.on('new_message', handleNewMessage);
    const updateRef = channel.on('message_updated', handleMessageUpdate);
    const deleteRef = channel.on('message_deleted', handleMessageDelete);
    const reactionRef = channel.on('reaction_updated', handleReactionUpdate);
    const typingStartRef = channel.on('typing_start', handleTypingStart);
    const typingStopRef = channel.on('typing_stop', handleTypingStop);

    return () => {
      channel.off('new_message', newMsgRef);
      channel.off('message_updated', updateRef);
      channel.off('message_deleted', deleteRef);
      channel.off('reaction_updated', reactionRef);
      channel.off('typing_start', typingStartRef);
      channel.off('typing_stop', typingStopRef);
    };
  }, [currentChannel?.id]);

  useEffect(() => {
    if (isLoading || initialScrollDoneRef.current || localMessages.length === 0) return;
    initialScrollDoneRef.current = true;
    // Double rAF ensures the DOM has fully laid out before scrolling
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom('auto');
      });
    });
  }, [isLoading, localMessages]);

  // Close reaction picker on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(e.target as Node)) {
        setReactionPickerMessageId(null);
      }
    };
    if (reactionPickerMessageId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [reactionPickerMessageId]);

  const handleReply = (messageId: string) => {
    setReplyTo(messageId);
  };

  const handleEdit = (messageId: string) => {
    setEditingMessageId(messageId);
  };

  const handleDelete = async (messageId: string) => {
    if (!currentChannel?.id) return;
    if (window.confirm('Are you sure you want to delete this message?')) {
      setLocalMessages(prev => prev.filter(msg => msg.id !== messageId));
      await useMessageStore.getState().deleteMessage(messageId, false, currentChannel.id, currentServer?.id);
    }
  };

  const getReplyMessage = (replyId: string) => {
    return localMessages.find(msg => msg.id === replyId);
  };

  const renderComponentEmoji = (emoji: any) => {
    if (!emoji) return null;
    return <span className="leading-none">{emoji.name || emoji.id || emoji}</span>;
  };

  const componentOptions = (component: any) => {
    const type = Number(component.type || 0);
    if (type === 3) {
      return Array.isArray(component.options)
        ? component.options.map((option: any) => ({
            value: String(option.value ?? option.label ?? ''),
            label: option.label || String(option.value ?? ''),
            description: option.description,
            emoji: option.emoji,
          }))
        : [];
    }
    if (type === 5) {
      return (members || []).map((member: any) => ({
        value: member.user_id,
        label: member.user?.username || member.user_id,
      }));
    }
    if (type === 6) {
      return (roles || []).map((role: any) => ({
        value: role.id,
        label: role.name,
      }));
    }
    if (type === 7) {
      return [
        ...(members || []).map((member: any) => ({ value: member.user_id, label: member.user?.username || member.user_id })),
        ...(roles || []).map((role: any) => ({ value: role.id, label: role.name })),
      ];
    }
    if (type === 8) {
      return (channels || []).map((channel: any) => ({
        value: channel.id,
        label: `#${channel.name}`,
      }));
    }
    return [];
  };

  const sendComponentInteraction = async (message: any, component: any, values?: string[]) => {
    if (!currentChannel?.id) return;
    const applicationId = message.application_id || message.user_id || message.author?.user_id || message.author?.id;
    const customId = String(component.custom_id || '');
    const componentType = Number(component.type || 0);
    if (!applicationId || !customId) return;
    const pendingKey = `${message.id}:${customId}`;
    setPendingComponentId(pendingKey);
    pendingComponentInteractionRef.current = null;
    if (pendingComponentTimeoutRef.current) clearTimeout(pendingComponentTimeoutRef.current);
    try {
      const response = await useMessageStore.getState().messagesAPI.createChannelInteraction(currentChannel.id, {
        application_id: applicationId,
        type: 3,
        message_id: message.id,
        custom_id: customId,
        component_type: componentType,
        data: { component_type: componentType, custom_id: customId, message_id: message.id, values, message },
      });
      pendingComponentInteractionRef.current = response?.id || null;
      pendingComponentTimeoutRef.current = setTimeout(() => {
        if (!pendingComponentInteractionRef.current || pendingComponentInteractionRef.current === response?.id) {
          setPendingComponentId(null);
          pendingComponentInteractionRef.current = null;
        }
        pendingComponentTimeoutRef.current = null;
      }, 15_000);
    } catch {
      setPendingComponentId(null);
      pendingComponentInteractionRef.current = null;
    }
  };

  const renderSelectComponent = (message: any, component: any, index: number) => {
    const customId = String(component.custom_id || '');
    const options = componentOptions(component);
    const isMessagePending = pendingComponentId?.startsWith(`${message.id}:`);
    const disabled = !!component.disabled || isMessagePending || !customId;
    const multiple = Number(component.max_values || 1) > 1;
    return (
      <select
        key={`${customId || index}`}
        multiple={multiple}
        disabled={disabled}
        defaultValue={multiple ? [] : ''}
        className="h-9 max-w-full rounded-md border border-border-color bg-bg-tertiary px-3 text-sm text-text-primary disabled:cursor-not-allowed disabled:opacity-60"
        onChange={(event) => {
          const values = Array.from(event.currentTarget.selectedOptions).map((option) => option.value).filter(Boolean);
          if (!values.length) return;
          void sendComponentInteraction(message, component, values);
          if (!multiple) event.currentTarget.value = '';
        }}
      >
        <option value="" disabled>{component.placeholder || 'Select an option'}</option>
        {options.map((option: any) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  };

  const renderMessageComponents = (message: any) => {
    const rows = Array.isArray(message.components) ? message.components : [];
    if (!rows.length || !currentChannel?.id) return null;
    const isMessagePending = pendingComponentId?.startsWith(`${message.id}:`) || false;
    const styleClasses: Record<number, string> = {
      1: 'bg-[#5865f2] text-white hover:bg-[#4752c4]',
      2: 'bg-bg-tertiary text-text-primary hover:bg-highlight border border-border-color',
      3: 'bg-[#248046] text-white hover:bg-[#1a6334]',
      4: 'bg-error text-white hover:bg-error/80',
      5: 'bg-bg-tertiary text-text-primary hover:bg-highlight border border-border-color',
    };

    return (
      <div className="mt-2 space-y-2">
        {rows.map((row: any, rowIndex: number) => {
          const children = Array.isArray(row?.components)
            ? row.components
            : row?.type === 2
              ? [row]
              : [];
          if (!children.length) return null;
          return (
            <div key={`components-${message.id}-${rowIndex}`} className="flex flex-wrap gap-2">
              {children.filter((component: any) => Number(component?.type || 0) >= 2 && Number(component?.type || 0) <= 8 && Number(component?.type || 0) !== 4).map((button: any, index: number) => {
                if (Number(button.type || 0) !== 2) return renderSelectComponent(message, button, index);
                const style = Number(button.style || 2);
                const customId = String(button.custom_id || '');
                const pendingKey = `${message.id}:${customId}`;
                const isPending = pendingComponentId === pendingKey;
                const applicationId = message.application_id || message.user_id || message.author?.user_id || message.author?.id;
                const disabled = !!button.disabled || isMessagePending || (style !== 5 && !customId) || !applicationId;
                return (
                  <button
                    key={`${customId || button.url || index}`}
                    type="button"
                    disabled={disabled}
                    onClick={async () => {
                      if (style === 5 && button.url) {
                        window.open(button.url, '_blank', 'noopener');
                        return;
                      }
                      if (!customId || !applicationId) return;
                      await sendComponentInteraction(message, button);
                    }}
                    className={`inline-flex h-8 max-w-full items-center gap-1.5 rounded px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${styleClasses[style] || styleClasses[2]}`}
                  >
                    {isPending && (
                      <span className="h-3.5 w-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
                    )}
                    {renderComponentEmoji(button.emoji)}
                    {button.label && <span className="truncate">{button.label}</span>}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const renderEphemeralMessage = (message: any) => {
    const author = message.author || {};
    const timestamp = message.created_at || message.timestamp;
    return (
      <div key={`ephemeral-${message.id}`} className="group relative mt-4 first:mt-0">
        <div className="px-4 py-1 rounded-md bg-accent-color/5 border-l-2 border-accent-color/60">
          {renderInteractionHeader(message.interaction_metadata)}
          <div className="flex items-start">
            <div className="avatar w-10 h-10 flex-shrink-0 mr-3">
              {author.avatar_url ? (
                <img
                  src={author.avatar_url}
                  alt={author.display_name || author.username || 'Bot'}
                  className="w-10 h-10 object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-accent-color text-white flex items-center justify-center rounded-full font-bold">
                  {(author.display_name || author.username || 'B').charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center">
                <span className="font-medium text-text-primary">
                  {author.display_name || author.username || 'Application'}
                </span>
                <AppBadge className="ml-2" />
                <span className="ml-2 text-[11px] text-text-muted" title={formatRelativeDate(timestamp).tooltip}>
                  {formatRelativeDate(timestamp).label}
                </span>
                <button
                  type="button"
                  className="ml-auto w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 transition-colors"
                  onClick={() => setEphemeralMessages((prev) => prev.filter((item) => item.id !== message.id))}
                  title="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="text-text-primary break-words text-[15px] leading-relaxed mt-0.5">
                <MessageProcessor content={message.content || ''} />
              </div>
              <DiscordEmbeds embeds={message.embeds} />
              {renderMessageComponents(message)}
              <div className="mt-1.5 text-xs text-text-muted">
                Only you can see this
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderInteractionHeader = (metadata: any) => {
    if (!metadata?.id) return null;
    const invocationUser = metadata.user || {};
    const label = metadata.command_display || (metadata.name ? `/${metadata.name}` : '/command');
    return (
      <div className="mb-1 ml-[52px] flex min-w-0 items-center text-xs text-text-muted">
        <div className="mr-1.5 h-3 w-5 flex-shrink-0 rounded-tl-md border-l-2 border-t-2 border-text-muted/30" />
        {invocationUser.avatar_url ? (
          <img
            src={invocationUser.avatar_url}
            alt={invocationUser.display_name || invocationUser.username || 'User'}
            className="mr-1.5 h-4 w-4 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <span className="mr-1.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-bg-tertiary text-[9px] font-bold text-text-secondary">
            {(invocationUser.display_name || invocationUser.username || '?').charAt(0).toUpperCase()}
          </span>
        )}
        <span className="min-w-0 truncate">
          <span className="font-medium text-text-secondary">
            {invocationUser.display_name || invocationUser.global_name || invocationUser.username || 'Utilisateur'}
          </span>
          <span> a utilisé </span>
          <span className="font-semibold text-accent-color">{label}</span>
        </span>
      </div>
    );
  };

  const renderPendingInteraction = (item: any) => {
    const application = item.application || item.interaction_metadata?.application || {};
    const botName = application.name || 'Application';
    const botAvatar = application.avatar;
    const isFailed = !!item.failed;
    return (
      <div key={`pending-${item.interaction_id}`} className="group relative mt-4 first:mt-0">
        <div className={`px-4 py-1 opacity-90 ${isFailed ? 'rounded-md bg-accent-color/5 border-l-2 border-accent-color/60' : ''}`}>
          {renderInteractionHeader(item.interaction_metadata)}
          <div className="flex items-start">
          <div className="avatar w-10 h-10 flex-shrink-0 mr-3">
            {botAvatar ? (
              <img src={botAvatar} alt={botName} className="w-10 h-10 object-cover" />
            ) : (
              <div className="w-10 h-10 bg-accent-color text-white flex items-center justify-center rounded-full font-bold">
                {botName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center">
              <span className="font-medium text-text-primary">{botName}</span>
              <AppBadge className="ml-2" />
            </div>
            <div className={`text-[15px] leading-relaxed ${isFailed ? 'text-error' : 'text-text-muted'}`}>
              {isFailed ? "L'application n'a pas répondu." : (
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex gap-[2px] items-center">
                    <span className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce [animation-delay:0ms]" />
                    <span className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce [animation-delay:150ms]" />
                    <span className="w-1.5 h-1.5 bg-text-muted rounded-full animate-bounce [animation-delay:300ms]" />
                  </span>
                  <span>Envoi de la commande...</span>
                </span>
              )}
            </div>
            {isFailed && (
              <div className="mt-1.5 text-xs text-text-muted">
                Only you can see this
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
    );
  };

  const timelineItems = [
    ...localMessages.map((message) => ({ ...message, __ephemeral: false })),
    ...ephemeralMessages.map((message) => ({
      ...message,
      __ephemeral: true,
      created_at: message.created_at || message.timestamp || new Date(getMessageSortTime(message)).toISOString(),
    })),
    ...pendingInteractions.map((message) => ({
      ...message,
      id: message.id || `pending-${message.interaction_id}`,
      created_at: message.created_at || new Date().toISOString(),
    })),
  ].sort(compareTimelineItems);

  const previousChannelMessage = (index: number) => {
    for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
      if (!timelineItems[cursor].__ephemeral && !timelineItems[cursor].__pending) return timelineItems[cursor];
    }
    return null;
  };

  const modalTextInputs = (activeModal?.data?.components || [])
    .flatMap((row: any) => Array.isArray(row?.components) ? row.components : [])
    .filter((component: any) => Number(component?.type || 0) === 4);

  const submitActiveModal = async () => {
    if (!activeModal || !currentChannel?.id) return;
    for (const input of modalTextInputs) {
      const value = modalValues[input.custom_id] ?? input.value ?? '';
      if (input.required && !String(value).trim()) return;
      if (input.min_length && String(value).length < Number(input.min_length)) return;
      if (input.max_length && String(value).length > Number(input.max_length)) return;
    }
    const components = (activeModal.data?.components || []).map((row: any) => ({
      type: 1,
      components: (row.components || [])
        .filter((component: any) => Number(component?.type || 0) === 4)
        .map((component: any) => ({
          type: 4,
          custom_id: component.custom_id,
          value: modalValues[component.custom_id] ?? '',
        })),
    }));
    await useMessageStore.getState().messagesAPI.createChannelInteraction(currentChannel.id, {
      application_id: activeModal.application_id,
      type: 5,
      custom_id: activeModal.data?.custom_id,
      data: {
        application_id: activeModal.application_id,
        custom_id: activeModal.data?.custom_id,
        components,
        message: activeModal.message,
      },
    });
    setActiveModal(null);
    setModalValues({});
  };

  if (!currentChannel) {
    return (
      <div className="flex-1 flex items-center justify-center bg-bg-primary text-text-secondary">
        Select a channel to start chatting
      </div>
    );
  }

  return (
    <>
    <div className="flex flex-col h-full bg-bg-primary">
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4"
      >
        {isLoading ? (
          <ChannelMessageSkeleton />
        ) : localMessages.length === 0 && ephemeralMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-text-secondary">
            <p className="text-center">No messages yet</p>
            <p className="text-sm">Be the first to send a message!</p>
          </div>
        ) : (
          <>
            {isLoadingMore && (
              <div className="text-center mb-2 text-xs text-text-muted">
                Loading older messages...
              </div>
            )}
            {timelineItems.map((message, index) => {
              if (message.__pending) return renderPendingInteraction(message);
              if (message.__ephemeral) return renderEphemeralMessage(message);
              const prevMessage = previousChannelMessage(index);
		              const isSameAuthor = prevMessage && prevMessage.user_id === message.user_id;
		              const timeDiff = prevMessage ? (parseMessageDate(message.created_at).getTime() - parseMessageDate(prevMessage.created_at).getTime()) / 60000 : Infinity;
		              const isGrouped = isSameAuthor && timeDiff < 5 && !message.reply_to && !message.interaction_metadata?.id;
	              const isDeleted = !!message.deleted_at;
	
	              return (
              <div key={message.id} className={`group relative ${isGrouped ? '' : 'mt-4 first:mt-0'}`} onContextMenu={(e) => {
                e.preventDefault();
                setContextMenu({ position: { x: e.clientX, y: e.clientY }, messageId: message.id });
              }}>

              {/* Floating action bar */}
	              {!isDeleted && (
	              <div className="absolute -top-3 right-4 opacity-0 group-hover:opacity-100 transition-all duration-150 z-10">
                <div className="flex items-center gap-0.5 bg-bg-secondary border border-border-color rounded-lg shadow-lg px-1 py-0.5">
                  {canAddReactions && (
                  <button
                    className="w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 transition-colors"
                    onClick={() => setReactionPickerMessageId(
                      reactionPickerMessageId === message.id ? null : message.id
                    )}
                    title="Réagir"
                  >
                    <Smile className="h-4 w-4" />
                  </button>
                  )}
                  <button
                    className="w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 transition-colors"
                    onClick={() => handleReply(message.id)}
                    title="Répondre"
                  >
                    <Reply className="h-4 w-4" />
                  </button>
                  {user && message.user_id === user.id && (
                    <>
                      <button
                        className="w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 transition-colors"
                        onClick={() => handleEdit(message.id)}
                        title="Modifier"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        className="w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-error hover:bg-error/10 transition-colors"
                        onClick={() => handleDelete(message.id)}
                        title="Supprimer"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </>
                  )}
                  {user && message.user_id !== user.id && canManageMessages && (
                    <button
                      className="w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-error hover:bg-error/10 transition-colors"
                      onClick={() => handleDelete(message.id)}
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                  {user && message.user_id !== user.id && (
                    <button
                      className="w-7 h-7 rounded-md flex items-center justify-center text-text-muted hover:text-error hover:bg-error/10 transition-colors"
                      title="Signaler"
                      onClick={() => setReportingMessage(message)}
                    >
                      <Flag className="h-4 w-4" />
                    </button>
                  )}
	                </div>
	              </div>
	              )}
	
	              {/* Emoji picker portal */}
	              {canAddReactions && reactionPickerMessageId === message.id && !isDeleted && (
	                <div ref={reactionPickerRef} className="fixed right-4 bottom-24 z-[1000] w-[320px] max-w-[calc(100vw-2rem)]">
                  <EmojiPicker
                    onEmojiClick={(emojiData) => {
                      useMessageStore.getState().addReaction(message.id, emojiData.emoji, false, currentChannel.id);
                      setReactionPickerMessageId(null);
                    }}
                    theme={"dark" as any}
	                    width="100%"
                    height={400}
                  />
                </div>
              )}

              {message.reply_to && (
                <div className="ml-[52px] mb-1 text-xs text-text-muted flex items-center gap-1">
                  <div className="w-5 border-t-2 border-l-2 border-text-muted/30 rounded-tl-md h-3 mr-1" />
                  <span className="font-medium text-text-secondary">
                    {getReplyMessage(message.reply_to)?.author?.display_name || getReplyMessage(message.reply_to)?.author?.username || 'Unknown'}
                  </span>
                  <span className="truncate max-w-md text-text-muted">
                    {getReplyMessage(message.reply_to)?.content.substring(0, 50) || 'Message not found'}
                  </span>
                </div>
              )}
              {!isGrouped && renderInteractionHeader(message.interaction_metadata)}
              
              <div className={`flex items-start px-4 py-0.5 hover:bg-highlight/10 rounded-md transition-colors ${isGrouped ? '' : 'mt-1'}`}>
                {isGrouped ? (
                  <div className="w-10 flex-shrink-0 mr-3 flex items-center justify-center">
                    <span className="text-[10px] text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" title={formatRelativeDate(message.created_at).tooltip}>
                      {format(parseMessageDate(message.created_at), 'HH:mm')}
                    </span>
                  </div>
                ) : (
                  <UserProfileLink userId={message.user_id}>
                    <div className="avatar w-10 h-10 flex-shrink-0 mr-3">
                      {message.author?.avatar_url ? (
                        <img 
                          src={message.author.avatar_url} 
                          alt={message.author?.display_name || message.author?.username} 
                          className="w-10 h-10 object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-accent-color text-white flex items-center justify-center rounded-full">
                          {(message.author?.display_name || message.author?.username || '').charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                  </UserProfileLink>
                )}
                
                <div className="flex-1 min-w-0">
                  {!isGrouped && (
                    <div className="flex items-center">
                      <UserProfileLink userId={message.user_id}>
                        <span className="font-medium text-text-primary hover:underline" style={{ color: getAuthorRoleColor(message.user_id) || undefined }}>
                          {message.author?.display_name || message.author?.username || 'Unknown User'}
                        </span>
                      </UserProfileLink>
                      {!!message.author?.bot && <AppBadge className="ml-2" />}
                      <span className="ml-2 text-[11px] text-text-muted" title={formatRelativeDate(message.created_at).tooltip}>
                        {formatRelativeDate(message.created_at).label}
                      </span>
                      {message.edited && (
                        <span className="ml-1.5 text-[10px] text-text-muted">(modifié)</span>
                      )}
                    </div>
                  )}
                  
	                  {isDeleted ? (
	                    <div className={`text-text-muted italic break-words text-[15px] leading-relaxed ${isGrouped ? '' : 'mt-0.5'}`}>
	                      Message supprimé
	                    </div>
	                  ) : editingMessageId === message.id ? (
                    <MessageInput 
                      channelId={currentChannel.id}
                      initialValue={message.content}
                      onSend={(content) => {
                        useMessageStore.getState().editMessage(message.id, content, false, currentChannel.id, currentServer?.id);
                        setEditingMessageId(null);
                      }}
                      onCancel={() => setEditingMessageId(null)}
                      isEditing
                    />
                  ) : (
	                    <div className={`text-text-primary break-words text-[15px] leading-relaxed ${isGrouped ? '' : 'mt-0.5'}`}>
	                      <MessageProcessor
	                        content={message.content}
	                      />
	                    </div>
	                  )}

	                  {!isDeleted && <DiscordEmbeds embeds={message.embeds} />}
	                  {!isDeleted && renderMessageComponents(message)}

	                  {/* Interactive buttons (system messages, bots) */}
	                  {!message.components && message.buttons && Array.isArray(message.buttons) && message.buttons.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {message.buttons.map((btn: any) => {
                        const styleClasses =
                          btn.style === 'danger'
                            ? 'bg-error text-white hover:bg-error/80'
                            : btn.style === 'secondary'
                              ? 'bg-bg-tertiary text-text-secondary hover:bg-highlight/50'
                              : 'bg-accent-color text-black hover:bg-accent-color/80';
                        return (
                          <button
                            key={btn.id}
                            onClick={() => {
                              if (btn.action === 'url' && btn.payload) {
                                window.open(btn.payload, '_blank', 'noopener');
                              }
                            }}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${styleClasses}`}
                          >
                            {btn.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Discord-style reactions */}
	                  {!isDeleted && message.reactions && Object.keys(message.reactions).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {Object.entries(message.reactions).map(([emoji, users]) => {
                        const userArray = Array.isArray(users) ? users as string[] : [];
                        const isActive = user ? userArray.includes(user.id) : false;
                        return (
                          <button
                            key={emoji}
                            className={`flex items-center gap-1 rounded-md px-2 py-0.5 text-sm transition-all ${
                              isActive
                                ? 'bg-accent-color/15 border border-accent-color/40 text-text-primary'
                                : 'bg-bg-tertiary/80 hover:bg-highlight border border-transparent hover:border-border-color text-text-secondary'
                            }`}
                            disabled={!canAddReactions}
                            onClick={() => {
                              if (!canAddReactions) return;
                              if (isActive) {
                                useMessageStore.getState().removeReaction(message.id, emoji, false, currentChannel.id);
                              } else {
                                useMessageStore.getState().addReaction(message.id, emoji, false, currentChannel.id);
                              }
                            }}
                          >
                            <span>{emoji}</span>
                            <span className="text-xs font-medium">{userArray.length}</span>
                          </button>
                        );
                      })}
                      {canAddReactions && (
                      <button
                        className="flex items-center justify-center w-7 h-7 rounded-md bg-bg-tertiary/60 hover:bg-highlight text-text-muted hover:text-text-primary transition-all border border-transparent hover:border-border-color"
                        onClick={() => setReactionPickerMessageId(message.id)}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
	              </div>
	              );
	            })}
          </>
        )}
      </div>

      <TypingIndicator roomKey={`ch:${currentChannel.id}`} />
      <div className="px-4 py-3">
        {replyTo && (
          <div className="mb-2 bg-bg-tertiary/50 border border-border-color/50 px-3 py-2 rounded-lg flex justify-between items-center">
            <div className="flex items-center text-sm text-text-secondary">
              <Reply className="h-4 w-4 mr-2 text-accent-color" />
              <span className="text-text-muted">Répondre à</span>
              <span className="font-medium mx-1.5 text-text-primary">
                {getReplyMessage(replyTo)?.author?.display_name || getReplyMessage(replyTo)?.author?.username || 'Unknown'}
              </span>
              <span className="truncate max-w-md text-text-muted">
                {getReplyMessage(replyTo)?.content.substring(0, 50) || 'Message not found'}
              </span>
            </div>
            <button
              className="text-text-muted hover:text-text-primary transition-colors ml-2"
              onClick={() => setReplyTo(null)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        
        <MessageInput 
          channelId={currentChannel.id}
          replyTo={replyTo}
          onSend={() => setReplyTo(null)}
          disabled={!canSendMessages}
          disabledReason="You don't have permission to send messages in this channel."
          canAttachFiles={canAttachFiles}
        />
      </div>
    </div>

      {reportingMessage && (
        <ReportModal
          target={{
            type: 'message',
            id: reportingMessage.id,
            reported_user_id: reportingMessage.user_id,
            label: `@${reportingMessage.author?.display_name || reportingMessage.author?.username || 'inconnu'} · #${currentChannel?.name || ''}`,
            content: reportingMessage.content?.substring(0, 300),
          }}
          onClose={() => setReportingMessage(null)}
        />
      )}

      {activeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="w-full max-w-md rounded-lg border border-border-color bg-bg-secondary shadow-2xl">
            <div className="border-b border-border-color px-4 py-3">
              <h2 className="text-base font-semibold text-text-primary">{activeModal.data?.title || 'Form'}</h2>
            </div>
            <div className="space-y-4 px-4 py-4">
              {modalTextInputs.map((input: any) => {
                const value = modalValues[input.custom_id] ?? input.value ?? '';
                const isParagraph = Number(input.style || 1) === 2;
                const commonClass = "w-full rounded-md border border-border-color bg-bg-primary px-3 py-2 text-sm text-text-primary outline-none focus:border-accent-color";
                return (
                  <label key={input.custom_id} className="block">
                    <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-text-secondary">
                      {input.label}{input.required ? <span className="text-error"> *</span> : null}
                    </span>
                    {isParagraph ? (
                      <textarea
                        className={`${commonClass} min-h-24 resize-y`}
                        placeholder={input.placeholder || ''}
                        minLength={input.min_length}
                        maxLength={input.max_length}
                        required={!!input.required}
                        value={value}
                        onChange={(event) => setModalValues((prev) => ({ ...prev, [input.custom_id]: event.target.value }))}
                      />
                    ) : (
                      <input
                        className={commonClass}
                        placeholder={input.placeholder || ''}
                        minLength={input.min_length}
                        maxLength={input.max_length}
                        required={!!input.required}
                        value={value}
                        onChange={(event) => setModalValues((prev) => ({ ...prev, [input.custom_id]: event.target.value }))}
                      />
                    )}
                  </label>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 border-t border-border-color px-4 py-3">
              <button className="rounded-md px-3 py-2 text-sm text-text-secondary hover:bg-highlight" onClick={() => setActiveModal(null)}>
                Cancel
              </button>
              <button
                className="rounded-md bg-accent-color px-3 py-2 text-sm font-semibold text-black hover:bg-accent-color/80"
                onClick={() => void submitActiveModal()}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {contextMenu && (() => {
	        const msg = localMessages.find(m => m.id === contextMenu.messageId);
	        if (!msg) return null;
	        const isOwn = user && msg.user_id === user.id;
	        const isDeleted = !!msg.deleted_at;
          const messageCommandsByApp = applicationCommands
            .filter((command) => command.type === 3 && !isDeleted)
            .reduce<Record<string, any[]>>((acc, command) => {
              const appId = command.application_id || command.application?.id || 'application';
              acc[appId] = [...(acc[appId] || []), command];
              return acc;
            }, {});
          const contextCommandItems: ContextMenuItem[] = Object.keys(messageCommandsByApp).length > 0 ? [{
            label: 'Applications',
            icon: <Plus className="h-4 w-4" />,
            onClick: () => {},
            children: Object.entries(messageCommandsByApp).map(([appId, commands]) => {
              const application = commands[0]?.application || {};
              return {
                label: application.name || 'Application',
                avatar: application.avatar,
                onClick: () => {},
                children: commands.map((command) => ({
                  label: command.name,
                  icon: <Plus className="h-4 w-4" />,
                  onClick: () => {
                    if (!currentChannel?.id) return;
                    const targetId = msg.id;
                    void useMessageStore.getState().messagesAPI.createChannelInteraction(currentChannel.id, {
                      application_id: command.application_id || appId,
                      command_id: command.id,
                      type: 2,
                      data: {
                        id: command.id,
                        name: command.name,
                        type: command.type,
                        target_id: targetId,
                        resolved: { messages: { [targetId]: { id: targetId, content: msg.content } } },
                      },
                    });
                  },
                })),
              };
            }),
          }] : [];
	        const items: ContextMenuItem[] = [
          ...contextCommandItems,
          {
	            label: 'Reply',
	            icon: <Reply className="h-4 w-4" />,
	            onClick: () => handleReply(msg.id),
	            hidden: isDeleted,
              separator: contextCommandItems.length > 0,
	          },
          {
	            label: 'Copy Text',
	            icon: <Copy className="h-4 w-4" />,
	            onClick: () => navigator.clipboard.writeText(msg.content),
	            hidden: isDeleted,
	          },
          {
	            label: 'Edit Message',
	            icon: <Edit className="h-4 w-4" />,
	            onClick: () => handleEdit(msg.id),
	            hidden: (!isOwn && !canManageMessages) || isDeleted,
	          },
          {
            label: 'Delete Message',
            icon: <Trash2 className="h-4 w-4" />,
	            onClick: () => handleDelete(msg.id),
	            danger: true,
	            hidden: !isOwn || isDeleted,
	            separator: true,
	          },
          {
            label: 'Report Message',
            icon: <Flag className="h-4 w-4" />,
	            onClick: () => setReportingMessage(msg),
	            danger: true,
	            hidden: !!isOwn || isDeleted,
	            separator: true,
	          },
          {
            label: 'Copy Message ID',
            icon: <Copy className="h-4 w-4" />,
            onClick: () => navigator.clipboard.writeText(msg.id),
            hidden: !developerMode,
            separator: true,
          },
        ];
        return (
          <ContextMenu
            items={items}
            position={contextMenu.position}
            onClose={() => setContextMenu(null)}
          />
        );
      })()}
    </>
  );
};

export default MessageList;
